import {
  Briefcase,
  House,
  FileText,
  GraduationCap,
  HelpCircle,
  MessageSquare,
  Settings,
  LogOut,
  Sparkles,
  ChevronDown,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import { auth } from "../firebase";
import { signOut } from "firebase/auth";
import type { FirestoreUserData } from "../types/user";
import { isPremium } from "../utils/plan";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import FeedbackDialog from "./FeedbackDialog";


/** `also`: pages under this one that keep it highlighted */
/** `beta`: a small Beta tag after the name */
type NavItem = { name: string; icon: typeof Briefcase; to: string; also?: string[]; beta?: boolean };

// Sidebar groups. A group with a title gets a small uppercase heading.
const navGroups: { title?: string; items: NavItem[] }[] = [
  {
    items: [
      { name: "Home", icon: House, to: "/dashboard/home", also: ["/dashboard/roles"] },
      { name: "Applications", icon: Briefcase, to: "/dashboard/tracker" },
      { name: "Dream Companies", icon: Sparkles, to: "/dashboard/dreamcompanies" },
    ],
  },
  {
    title: "Prepare",
    items: [
      // Hidden for now; the page still works at /dashboard/networking
      // { name: "Networking", icon: Users, to: "/dashboard/networking" },
      { name: "Interview Prep", icon: GraduationCap, to: "/dashboard/prep", beta: true },
      { name: "Drafts", icon: FileText, to: "/dashboard/applications" },
      // { name: "Cold Emailing", icon: Mail, to: "/dashboard/coldemails" },
      // { name: "Coffee Chats", icon: Coffee, to: "/dashboard/coffeechats" },
    ],
  },
];

// Account menu items, styled like the tracker card menu
const MENU_ITEM = "rounded-[7px] px-2.5 py-2 text-melt-text1 focus:bg-melt-text1/[.06] [&_svg]:text-melt-text3";


// const Sidebar: React.FC = () => {
//   const navigate = useNavigate();   
type SidebarProps = {
  userRecord: FirestoreUserData | null;
};
const Sidebar: React.FC<SidebarProps> = ({ userRecord }) => {
  const navigate = useNavigate();    
  const location = useLocation();
  const [user, setUser] = useState(auth.currentUser);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
      if (currentUser) {
        try {
          await currentUser.reload();
        } catch {
          // ignore reload errors
        }
      }
      setUser(currentUser);
    });

    return () => unsubscribe();
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/login", { replace: true });
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const initials = (user?.displayName || user?.email || "U")
    .trim()
    .split(/\s+/)
    .map((s) => s[0]?.toUpperCase())
    .slice(0, 2)
    .join("");

  const displayName = user?.displayName || user?.email || "User";
  // const email = user?.email || "";

  const email =
    (typeof userRecord?.email === "string" && userRecord.email) ||
    user?.email ||
    "";

  const plan = isPremium(userRecord)
    ? { label: "✓ Premium Plan Active", className: "text-emerald-600" }
    : { label: "Basic Plan", className: "text-melt-text3" };

  const closeFeedback = useCallback(() => setFeedbackOpen(false), []);

  return (
    <aside className="w-52 h-screen bg-melt-sidebar border-r border-melt-text1/[.09] flex flex-col">
      {/* Logo */}
      <div className="flex items-center gap-2 p-6">
        <div className="pl-1 rounded-lg">
            <img src="/logo-medium.svg" alt="" className="h-6 w-6"/>
        </div>
        <h1 className="text-lg font-semibold text-melt-text1">Caramel</h1>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-3">
        {navGroups.map((group, gi) => (
          <div key={group.title ?? gi} className={gi > 0 ? "mt-4" : ""}>
            {group.title && (
              <p className="mb-2 px-3 text-xs font-medium tracking-wider text-melt-text3">
                {group.title}
              </p>
            )}
            <div className="space-y-px">
              {group.items.map((item) => {
                const isActive = location.pathname === item.to || !!item.also?.includes(location.pathname);
                const Icon = item.icon;

                return (
                  <button
                    key={item.name}
                    type="button"
                    onClick={() => navigate(item.to)}
                    className={`flex w-full items-center gap-3 rounded-md px-3 py-1.5 text-left text-sm text-melt-text1 transition ${
                      isActive ? "bg-melt-text1/[.08] font-medium" : "hover:bg-melt-text1/[.05]"
                    }`}
                    aria-current={isActive ? "page" : undefined}
                  >
                    <Icon size={18} />
                    <span className="whitespace-nowrap">{item.name}</span>
                    {item.beta && (
                      <span className="-ml-1.5 rounded-[4px] bg-melt-peach px-1 py-px font-plex text-[9px] font-medium uppercase tracking-wide text-[#8A5210]">
                        Beta
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* Account (settings, help, feedback, log out) */}
      {user && (
        <div className="border-t border-melt-text1/[.09]">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="group flex w-full items-center justify-between gap-3 px-6 py-4 outline-none transition hover:bg-melt-text1/[.05] focus-visible:bg-melt-text1/[.05] data-[state=open]:bg-melt-text1/[.05]">
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-melt-caramel text-xs font-medium text-white">
                    {initials}
                  </div>
                  <span className="truncate text-sm font-medium text-melt-text1">{displayName}</span>
                </div>
                <ChevronDown
                  size={16}
                  className="shrink-0 text-melt-text2 transition-transform group-data-[state=open]:rotate-180"
                />
              </button>
            </DropdownMenuTrigger>

            <DropdownMenuContent
              side="top"
              align="center"
              className="w-48 rounded-[11px] border-melt-hair bg-melt-menu p-[5px] shadow-[0_18px_34px_-18px_rgba(92,52,23,.5)]"
            >
              <DropdownMenuLabel className="space-y-0.5 font-normal">
                <p className="truncate text-xs text-melt-text3">{email}</p>
                <p className={`text-[11px] font-medium ${plan.className}`}>{plan.label}</p>
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-melt-hair" />

              <DropdownMenuItem className={MENU_ITEM} onSelect={() => navigate("/settings")}>
                <Settings size={16} />
                Profile Settings
              </DropdownMenuItem>
              <DropdownMenuItem className={MENU_ITEM} onSelect={() => window.open("mailto:help@oncaramel.com?subject=Help", "_blank")}>
                <HelpCircle size={16} />
                Get help
              </DropdownMenuItem>
              <DropdownMenuItem className={MENU_ITEM} onSelect={() => setFeedbackOpen(true)}>
                <MessageSquare size={16} />
                Send feedback
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-melt-hair" />

              <DropdownMenuItem
                className={`${MENU_ITEM} text-[#A5492E] focus:bg-[#A5492E]/[.07] [&_svg]:text-[#A5492E]`}
                onSelect={handleLogout}
              >
                <LogOut size={16} />
                Log Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <FeedbackDialog
            open={feedbackOpen}
            plan={userRecord?.planSource ? String(userRecord.planSource) : String(userRecord?.plan ?? "")}
            onClose={closeFeedback}
          />
        </div>
      )}
    </aside>
  );
};

export default Sidebar;
